import React, {useContext, useEffect, useState} from 'react';
import {useFormContext, useFormState} from 'react-hook-form';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import classnames from '@src/helpers/classnames';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import useFormValue from '@src/hooks/useFormValue';
import Translate from '@src/components/Translate/Translate';
import RenderIf from '@src/components/RenderIf/RenderIf';
import trans from '@src/helpers/trans';
import {LocaleContext} from '@src/contexts/LocaleContext';
import {joinArrayOfReactNodes} from '@src/helpers/joinArrayOfReactNodes';

const dashedStyle = {
    borderBottom: '1px dashed',
    cursor: 'pointer',
} as React.CSSProperties;

export function CustomPriceField({rowIndex: index, isDisabled, className}: {
    rowIndex: number,
    isDisabled?: boolean,
    className?: string,
}) {
    const {
        fieldName,
        forceDisabled,
        forceReadonly
    } = useContext<IFormFieldItemPropsContext<IOrderModel> | null>(FormFieldItemPropsContext) ?? {};
    const {locale} = useContext(LocaleContext);
    const {control, setValue, register} = useFormContext<IOrderModel>();
    const [design] = useContext(FormFieldDesignContext);
    const {errors} = useFormState<IOrderModel>({control});

    const inputName = `${fieldName}.${index}.custom_price`;
    const priceTypeFieldName = `${fieldName}.${index}.custom_price_type`;

    const {currentValue: isFrozen} = useFormValue<IOrderModel>(`${fieldName}.${index}.is_price_frozen`);
    const {currentValue: priceType = 'per-pack'} = useFormValue<IOrderModel>(priceTypeFieldName);
    const {currentValue} = useFormValue<IOrderModel>(inputName);

    const [initialValue, setInitialValue] = useState(currentValue);
    const [disabled, setDisabled] = useState(currentValue == null);

    useEffect(() => {
        if (initialValue == null && currentValue != null) {
            setInitialValue(currentValue);
        }
    }, [currentValue, initialValue]);

    useEffect(() => {
        setDisabled(currentValue == null);
    }, [currentValue]);

    useEffect(() => {
        if (!disabled) {
            setValue(inputName, initialValue);
        } else if (disabled) {
            setValue(inputName, null);
        }
    }, [disabled, setValue, inputName, initialValue]);

    const notInteractive = (
            isFrozen ||
            isDisabled || design.fieldsAreDisabled || forceDisabled ||
            design.fieldsAreReadOnly || forceReadonly
    );

    return <div className={className}>
        <div className={disabled ? undefined : 'mb-2'}>
            <Translate
                    message="OrderForm.products.fields.custom_price"
                    style={dashedStyle}
                    className={classnames({'opacity-50': notInteractive})}
                    onClick={(notInteractive) ? undefined : () => {
                        setDisabled(v => !v);
                    }}
            />
            <RenderIf expression={!disabled && !notInteractive}>
                <span className="small ms-2">
                    [<span className="px-1">
                        {joinArrayOfReactNodes(['for-unit', 'for-pack'].map(name => {
                            const prefix = 'OrderForm.products.custom_price_type';
                            const message = `${prefix}_${name.replace('-', '_')}`;

                            return <Translate
                                    key={message}
                                    message={message}
                                    className={classnames('cursor-pointer', {'opacity-50': priceType !== name})}
                                    onClick={() => setValue(priceTypeFieldName, name)}
                            />;
                        }), ' / ')}
                    </span>]
                </span>
            </RenderIf>
        </div>

        <RenderIf expression={!disabled}>
            <input
                    type="number"
                    placeholder={trans('OrderForm.products.fields.custom_price', undefined, locale)}
                    className={classnames('form-control', {
                        'is-invalid': Boolean(errors.products?.[index]?.custom_price)
                    })}
                    min={0}
                    step={0.01}
                    {...register(inputName)}
                    disabled={isFrozen || isDisabled || disabled || design.fieldsAreDisabled || forceDisabled}
                    readOnly={design.fieldsAreReadOnly || forceReadonly}
            />
        </RenderIf>
    </div>;
}
